import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BlogBackToTopButton from '../components/BlogBackToTopButton';
import { API_BASE_URL, apiUrl } from '../config';

function formatDate(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).format(new Date(value));
  } catch (_) {
    return '';
  }
}

function formatDateUpper(value) {
  const formatted = formatDate(value);
  return formatted ? formatted.toLocaleUpperCase('tr-TR') : '';
}

function resolveBlogImageUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.startsWith('/uploads/') || raw.startsWith('/api/uploads/')) return `${API_BASE_URL}${raw}`;
  if (raw.startsWith('uploads/')) return `${API_BASE_URL}/${raw}`;
  return raw;
}

function FallbackVisual({ eyebrow, title, chips = [] }) {
  return (
    <div className="flex h-full flex-col justify-between bg-[radial-gradient(circle_at_top_left,_rgba(248,113,113,0.24),_transparent_42%),linear-gradient(135deg,_rgba(15,23,42,0.98),_rgba(30,41,59,0.88))] p-6 text-white">
      <div className="inline-flex w-fit rounded-full border border-white/15 bg-white/10 px-3 py-2 text-[11px] font-black uppercase tracking-[0.24em] text-white/80">
        {eyebrow}
      </div>
      <div>
        <div className="max-w-sm font-serif text-3xl font-black leading-tight tracking-tight">
          {title}
        </div>
        {chips.length ? (
          <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-white/70">
            {chips.slice(0, 3).map((chip) => (
              <span key={chip} className="rounded-full border border-white/15 px-3 py-2">
                {chip}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function BlogLandingPage({ isDarkMode, toggleDarkMode }) {
  const [payload, setPayload] = useState({ featuredPost: null, posts: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [featuredIndex, setFeaturedIndex] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetch(apiUrl('/api/blog'));
        if (!res.ok) throw new Error('blog_fetch_failed');
        const data = await res.json();
        if (!mounted) return;
        setPayload({
          featuredPost: data?.featuredPost || null,
          posts: Array.isArray(data?.posts) ? data.posts : [],
          categories: Array.isArray(data?.categories) ? data.categories : []
        });
      } catch (_error) {
        if (!mounted) return;
        setError('Blog içerikleri şu anda yüklenemedi.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    document.title = 'Blog | Bana Bir Tarif Ver';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', 'BBTV blog içinde mutfak yönetimi, yemek planlama, stok, bütçe ve güvenli ev rutini içeriklerini keşfedin.');
    }
  }, []);

  const featuredPosts = useMemo(() => {
    const rows = (payload.posts || []).filter((post) => Boolean(post.isFeatured));
    const limited = rows.slice(0, 3);
    return limited.length ? limited : (payload.featuredPost ? [payload.featuredPost] : []);
  }, [payload.posts, payload.featuredPost]);

  const featuredPost = featuredPosts[featuredIndex] || null;
  const listPosts = useMemo(() => {
    const featuredIds = new Set(featuredPosts.map((post) => post.id));
    return (payload.posts || []).filter((post) => !featuredIds.has(post.id));
  }, [payload.posts, featuredPosts]);

  useEffect(() => {
    if (featuredPosts.length <= 1) {
      setFeaturedIndex(0);
      return undefined;
    }

    setFeaturedIndex((current) => (current >= featuredPosts.length ? 0 : current));
    const timer = window.setInterval(() => {
      setFeaturedIndex((current) => (current + 1) % featuredPosts.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [featuredPosts]);

  return (
    <div className="min-h-screen bg-white text-slate-900 dark:bg-[#1a1a1a] dark:text-white">
      <Header isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />

      <main className="mx-auto max-w-[1320px] px-6 pb-20 pt-24 md:px-8 md:pt-28 xl:px-10">
        <div className="mb-8 border-l-4 border-primary pl-4">
          <p className="mb-2 text-[11px] font-black uppercase tracking-[0.28em] text-primary">Yayınlar</p>
          <h1 className="font-serif text-4xl font-black tracking-tight text-slate-950 dark:text-white md:text-6xl">
            Mutfak Yönetimi İçerik Merkezi
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
            Tarif aramaktan öte, ev mutfağını stok, bütçe, porsiyon, güvenli planlama ve haftalık rutin üzerinden yönetmek isteyenler için hazırlanmış kategori ve yazılar.
          </p>
        </div>

        {loading ? (
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-sm dark:border-slate-800 dark:bg-slate-900/60">
            Blog içerikleri yükleniyor...
          </section>
        ) : error ? (
          <section className="rounded-xl border border-red-200 bg-red-50 p-8 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-300">
            {error}
          </section>
        ) : (
          <>
            {(payload.categories || []).length > 0 ? (
              <section className="mb-10">
                <div className="mb-4 text-[11px] font-black uppercase tracking-[0.28em] text-slate-500 dark:text-slate-400">
                  Ana Kategoriler
                </div>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {payload.categories.map((category) => (
                    <Link
                      key={category.id}
                      to={category.canonicalUrl || `/blog/${category.slug}`}
                      className="group rounded-[1.35rem] border border-slate-200 bg-white p-4 transition hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_18px_45px_-28px_rgba(239,68,68,0.55)] dark:border-slate-800 dark:bg-slate-950"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <h2 className="font-serif text-xl font-black tracking-tight text-slate-950 dark:text-white">
                          {category.name}
                        </h2>
                        <span className="material-symbols-outlined text-lg text-slate-400 transition group-hover:text-primary">arrow_outward</span>
                      </div>
                      <p className="mt-2 line-clamp-2 text-[15px] leading-6 text-slate-600 dark:text-slate-300">
                        {category.description}
                      </p>
                    </Link>
                  ))}
                </div>
              </section>
            ) : null}

            {featuredPost && (
              <section className="group mb-20 grid grid-cols-1 gap-6 lg:grid-cols-12">
                <Link to={featuredPost.canonicalUrl || `/blog/${featuredPost.categorySlug}/${featuredPost.slug}`} className="overflow-hidden rounded-[1.6rem] lg:col-span-8">
                  <div className="h-[400px] overflow-hidden rounded-[1.6rem] bg-slate-100 dark:bg-slate-800">
                    {featuredPost.coverImageUrl ? (
                      <img
                        src={resolveBlogImageUrl(featuredPost.coverImageUrl)}
                        alt={featuredPost.coverImageAlt || featuredPost.title}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full flex-col justify-between bg-[radial-gradient(circle_at_top_left,_rgba(248,113,113,0.22),_transparent_42%),linear-gradient(135deg,_rgba(15,23,42,0.96),_rgba(30,41,59,0.88))] p-8 text-white">
                        <div className="inline-flex w-fit rounded-full border border-white/15 bg-white/10 px-3 py-2 text-[11px] font-black uppercase tracking-[0.24em] text-white/80">
                          BBTV İçerik Merkezi
                        </div>
                        <div>
                          <div className="max-w-md font-serif text-4xl font-black leading-tight tracking-tight">
                            Stok + Bütçe + Porsiyon ile mutfak kararı
                          </div>
                          <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-white/70">
                            <span className="rounded-full border border-white/15 px-3 py-2">Haftalık Plan</span>
                            <span className="rounded-full border border-white/15 px-3 py-2">Bugün Ne Pişirsem</span>
                            <span className="rounded-full border border-white/15 px-3 py-2">Bebekli Aileler</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Link>

                <div className="flex flex-col justify-center gap-4 lg:col-span-4">
                  {featuredPosts.length > 1 ? (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setFeaturedIndex((current) => (current - 1 + featuredPosts.length) % featuredPosts.length)}
                        className="rounded-full border border-slate-300 px-3 py-2 text-xs font-black text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                        aria-label="Önceki öne çıkan yazı"
                      >
                        ‹
                      </button>
                      <span className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                        {featuredIndex + 1}/{featuredPosts.length}
                      </span>
                      <button
                        type="button"
                        onClick={() => setFeaturedIndex((current) => (current + 1) % featuredPosts.length)}
                        className="rounded-full border border-slate-300 px-3 py-2 text-xs font-black text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                        aria-label="Sonraki öne çıkan yazı"
                      >
                        ›
                      </button>
                    </div>
                  ) : null}
                  <span className="text-[11px] font-black uppercase tracking-[0.28em] text-primary">Öne Çıkan İçerik</span>
                  <Link to={featuredPost.canonicalUrl || `/blog/${featuredPost.categorySlug}/${featuredPost.slug}`} className="flex flex-col gap-4">
                    <h2 className="font-serif text-3xl font-black leading-tight tracking-tight text-slate-950 transition-colors group-hover:text-primary dark:text-white md:text-4xl">
                      {featuredPost.title}
                    </h2>
                    <p className="line-clamp-3 text-lg leading-8 text-slate-600 dark:text-slate-300">
                      {featuredPost.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-sm font-bold text-primary">
                      <span>Devamını Oku</span>
                      <span className="material-symbols-outlined text-base">arrow_forward</span>
                    </div>
                  </Link>
                </div>
              </section>
            )}

            <section className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {listPosts.map((post) => (
                <Link
                  key={post.id}
                  to={post.canonicalUrl || `/blog/${post.categorySlug}/${post.slug}`}
                  className="flex flex-col rounded-lg border border-slate-200 bg-white transition-transform duration-200 hover:-translate-y-1 dark:border-slate-800 dark:bg-slate-900"
                >
                  <div className="aspect-video overflow-hidden rounded-t-lg bg-slate-100 dark:bg-slate-800">
                    {post.coverImageUrl ? (
                      <img
                        src={resolveBlogImageUrl(post.coverImageUrl)}
                        alt={post.coverImageAlt || post.title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-slate-400">Kapak görseli</div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <span className="mb-2 text-[11px] font-black uppercase tracking-[0.28em] text-primary">
                      {post.categoryName || 'İçerik'}
                    </span>
                    <h3 className="mb-3 h-14 overflow-hidden font-serif text-2xl font-bold leading-tight text-slate-950 dark:text-white">
                      {post.title}
                    </h3>
                    <p className="mb-5 line-clamp-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
                      {post.excerpt}
                    </p>
                    <div className="mt-auto flex items-center justify-between border-t border-slate-200 pt-4 text-[11px] font-black uppercase tracking-[0.18em] text-slate-500 dark:border-slate-800 dark:text-slate-400">
                      <span>{formatDateUpper(post.publishedAt)}</span>
                      <span>{post.readingTimeMinutes} dk okuma</span>
                    </div>
                  </div>
                </Link>
              ))}
            </section>

            <section className="mt-16 rounded-[1.8rem] border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-8 text-center dark:border-slate-800 dark:from-slate-950 dark:to-slate-900">
              <h2 className="font-serif text-3xl font-black tracking-tight text-slate-950 dark:text-white">
                Mutfağını yönetmeye başla
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-slate-600 dark:text-slate-300">
                Tarif aramaktan öte, evde ne pişeceğini stok, bütçe, porsiyon ve günlük rutin üzerinden birlikte yönetmek için BBTV akışını dene.
              </p>
              <div className="mt-6 flex justify-center">
                <Link
                  to="/kayit-ol"
                  className="rounded-xl bg-primary px-6 py-3 text-sm font-black text-white transition hover:scale-[0.99]"
                >
                  Kayıt Ol
                </Link>
              </div>
            </section>
          </>
        )}
      </main>

      <BlogBackToTopButton />
      <Footer />
    </div>
  );
}
